
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { supabase } from "@/lib/supabase";
import { PlusCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthSession } from '@/hooks/use-auth-session';
import { useNavigate } from 'react-router-dom';

type PlaylistFormData = {
  name: string;
  description: string;
  city: string;
  country: string;
  isPublic: boolean;
};

interface CreatePlaylistDialogProps {
  isAuthenticated: boolean;
}

export function CreatePlaylistDialog({ isAuthenticated }: CreatePlaylistDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const session = useAuthSession();
  const navigate = useNavigate();
  const { register, handleSubmit, reset } = useForm<PlaylistFormData>();
  const [isPublic, setIsPublic] = React.useState(true);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleCreateClick = () => {
    if (!isAuthenticated) {
      navigate('/auth');
      toast({
        title: "Authentication Required",
        description: "Please sign in to create a collection",
      });
      return;
    }
    setIsOpen(true);
  };

  const onSubmit = async (data: PlaylistFormData) => {
    if (!session?.user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to create a collection",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('destination_playlists')
        .insert({
          name: data.name,
          description: data.description,
          city: data.city,
          country: data.country,
          is_public: isPublic,
          user_id: session.user.id,
          created_by_user_id: session.user.id  // Add this line to set created_by_user_id
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Collection created successfully",
      });

      queryClient.invalidateQueries({ queryKey: ['playlists'] });
      reset();
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating collection:', error);
      toast({
        title: "Error",
        description: "Failed to create collection",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Button className="gap-2" onClick={handleCreateClick}>
        <PlusCircle className="w-4 h-4" />
        Create Collection
      </Button>
      
      {isAuthenticated && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Collection</DialogTitle>
              <DialogDescription>
                Create a new collection to organize your favorite destinations
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  {...register("name", { required: true })}
                  placeholder="My Travel Wishlist"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  {...register("city")}
                  placeholder="e.g., Paris"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  {...register("country")}
                  placeholder="e.g., France"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register("description")}
                  placeholder="A collection of places I want to visit..."
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="public">Public Collection</Label>
                  <div className="text-sm text-muted-foreground">
                    Allow others to view this collection
                  </div>
                </div>
                <Switch
                  id="public"
                  checked={isPublic}
                  onCheckedChange={setIsPublic}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Create Collection</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
